import React, { useEffect, useRef, useState } from "react";
import { Grid, IconButton, TextField, Typography } from "@mui/material";
import { decimalToString } from "../../../../utils/formatNumber";
import { injectIntl } from "react-intl";
import _ from "lodash";
import { NumberFormatWrapper } from "../../../../components/NumberFormatTextField/NumberFormatTextField";
import ColoredTopBorderCard from "../ColoredTopBorderCard/ColoredTopBorderCard";
import ToggleEditButton from "../ToggleEditButton/ToggleEditButton";
import produce from "immer";
import { usePrevious } from "../../../../utils/hooks";
import FolderDown from "lucide-react/dist/esm/icons/folder-down";
import {
  getPolicyCoverageForPolicyPicker,
  getPolicyMarketValue,
  getPolicyMonthlyPremiumForPolicyPicker,
  isPaidUpPolicy
} from "../../../../utils/policy";
import { PYRAMID_SECTION_MESSAGE_IDS } from "../constants";
import { focusRemainsInEditFlow, formatEditFieldLabel } from "../utils";

const HeaderTypoStyle = {
  opacity: 0.75
};

const ValueTypoStyle = {
  textOverflow: "ellipsis",
  overflowX: "hidden"
};

const PolicyPickerEditButtonStyle = {
  position: "absolute",
  top: "50%",
  right: -8,
  transform: "translateY(-50%)",
  padding: 4,
  zIndex: 1
};

const InputTableGrid = props => {
  const {
    intl,
    imgSrc,
    type,
    data,
    color,
    currency,
    onChange,
    cardBackground,
    openPolicyPicker,
    disabled
  } = props;

  const [isEdit, setIsEdit] = useState(false);
  const [internalData, setInternalData] = useState(_.cloneDeep(data));
  const [fieldToFocus, setFieldToFocus] = useState();
  // PolicyPicker mounts in a portal; the click that opens it often blurs with
  // relatedTarget=null before role="dialog" exists, which would auto-apply and
  // exit edit - then picker only writes internalData and never onChange.
  const suppressBlurApplyRef = useRef(false);
  const prevData = usePrevious(data);
  const canStartEditing = !!onChange && !disabled && !isEdit;
  const titleMessageId = PYRAMID_SECTION_MESSAGE_IDS[type];
  const title = titleMessageId ? intl.formatMessage({ id: titleMessageId }) : type;
  const editLabel = formatEditFieldLabel(intl, title);

  const applyChanges = forceApply => {
    if (onChange && (forceApply || !_.isEqual(internalData, data))) {
      onChange(internalData);
    }
    setIsEdit(false);
  };

  const startEditing = focusTarget => {
    if (!canStartEditing) {
      return;
    }

    setInternalData(_.cloneDeep(data));
    setFieldToFocus(focusTarget);
    setIsEdit(true);
  };

  const toggleIsEdit = () => {
    if (isEdit) {
      applyChanges(true);
    } else {
      startEditing();
    }
  };

  const handleDataAreaClick = event => {
    const fieldElement = event.target.closest("[data-edit-field]");
    startEditing(
      fieldElement
        ? {
            index: Number(fieldElement.dataset.editIndex),
            field: fieldElement.dataset.editField
          }
        : undefined
    );
  };

  const handleDataAreaKeyDown = event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      startEditing({ index: 0, field: "name" });
    }
  };

  const handleCardBlur = event => {
    if (!isEdit || suppressBlurApplyRef.current) {
      return;
    }

    if (focusRemainsInEditFlow(event)) {
      return;
    }

    applyChanges(false);
  };

  const createFieldOnChangeHandler = (index, field) => event => {
    const formattedValue = event.target.value;
    setInternalData(
      produce(internalData, draft => {
        draft[index][field] = formattedValue;
      })
    );
  };

  useEffect(() => {
    if (data !== prevData) {
      setInternalData(_.cloneDeep(data));
    }
  }, [data, prevData]);

  return (
    <Grid
      container
      wrap="nowrap"
      spacing={1.5}
      style={{ alignItems: "flex-start" }}
    >
      <Grid style={{ paddingTop: 4, flexShrink: 0 }}>
        <div
          style={{
            width: 28,
            height: 28,
            backgroundColor: color,
            maskImage: `url(${imgSrc})`,
            maskSize: "contain",
            maskRepeat: "no-repeat",
            maskPosition: "center",
            WebkitMaskImage: `url(${imgSrc})`,
            WebkitMaskSize: "contain",
            WebkitMaskRepeat: "no-repeat",
            WebkitMaskPosition: "center"
          }}
        />
      </Grid>
      <Grid style={{ flexGrow: 1, minWidth: 0 }} onBlur={handleCardBlur}>
        <ColoredTopBorderCard
          color={color}
          background={cardBackground}
          title={title}
          action={
            onChange && (
              <ToggleEditButton
                isEdit={isEdit}
                onChange={toggleIsEdit}
                disabled={disabled}
                style={{
                  background: isEdit ? "#fff" : "rgba(255, 255, 255, 0.2)",
                  color: isEdit ? color : "#fff",
                  boxShadow: "none",
                  borderRadius: 4,
                  padding: "2px 8px",
                  minWidth: "unset",
                  height: 22
                }}
              />
            )
          }
        >
          <Grid
            container
            spacing={1}
            sx={{
              flexDirection: "column",
              cursor: canStartEditing ? "text" : undefined
            }}
            onClick={canStartEditing ? handleDataAreaClick : undefined}
            onKeyDown={canStartEditing ? handleDataAreaKeyDown : undefined}
            role={canStartEditing ? "button" : undefined}
            tabIndex={canStartEditing ? 0 : undefined}
            aria-label={canStartEditing ? editLabel : undefined}
          >
            <Grid
              style={{
                borderBottom: "1px solid",
                borderBottomColor: color,
                whiteSpace: "nowrap"
              }}
            >
              <Grid container spacing={1}>
                <Grid size={5}>
                  <Typography color="textSecondary" style={HeaderTypoStyle}>
                    {intl.formatMessage({ id: "fin-py-item" })}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography color="textSecondary" style={HeaderTypoStyle}>
                    {type === "protection"
                      ? intl.formatMessage(
                          { id: "fin-py-input-coverage" },
                          { currency: intl.formatMessage({ id: currency }) }
                        )
                      : intl.formatMessage(
                          { id: "fin-py-net-value" },
                          { currency: intl.formatMessage({ id: currency }) }
                        )}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography color="textSecondary" style={HeaderTypoStyle}>
                    {intl.formatMessage(
                      { id: "fin-py-monthly-payment" },
                      { currency: intl.formatMessage({ id: currency }) }
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {(internalData || []).map((item, index) => {
              const { name, value, monthlyPayment } = item || {};

              if (!isEdit && !name && !value && !monthlyPayment) {
                return null;
              }

              const choosePolicies = () => {
                suppressBlurApplyRef.current = true;
                openPolicyPicker({
                  onClose: () => {
                    suppressBlurApplyRef.current = false;
                  },
                  onSubmit: policies => {
                    const { coverage, monthlyPremium } = (
                      policies || []
                    ).reduce(
                      (accumulator, policy) => {
                        const coverage =
                          (type === "protection"
                            ? getPolicyCoverageForPolicyPicker(policy, currency)
                            : getPolicyMarketValue(policy, currency)) || 0;
                        const monthlyPremium =
                          (!isPaidUpPolicy(policy)
                            ? getPolicyMonthlyPremiumForPolicyPicker(
                                policy,
                                currency
                              )
                            : 0) || 0;
                        accumulator.coverage += coverage;
                        accumulator.monthlyPremium += monthlyPremium;
                        return accumulator;
                      },
                      { coverage: 0, monthlyPremium: 0 }
                    );

                    setInternalData(
                      produce(internalData, draft => {
                        if ((policies || []).length === 1) {
                          draft[index].name = policies[0].policyName;
                        }

                        draft[index].value = Math.round(coverage);
                        draft[index].monthlyPayment = Math.round(
                          monthlyPremium
                        );
                      })
                    );
                  }
                });
              };

              return (
                <Grid key={index} style={{ position: "relative" }}>
                  {isEdit && (
                    <IconButton
                      style={PolicyPickerEditButtonStyle}
                      onClick={choosePolicies}
                    >
                      <FolderDown size={18} color="#9e9e9e" />
                    </IconButton>
                  )}
                  <Grid container spacing={1}>
                    <Grid size={5} style={{ minWidth: 0 }}>
                      {!isEdit ? (
                        <Typography
                          color="textSecondary"
                          style={ValueTypoStyle}
                          data-edit-index={index}
                          data-edit-field="name"
                        >
                          {name}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={name || ""}
                          autoFocus={
                            _.get(fieldToFocus, "index") === index &&
                            _.get(fieldToFocus, "field") === "name"
                          }
                          onChange={createFieldOnChangeHandler(index, "name")}
                          slotProps={{
                            htmlInput: { style: { marginBottom: 1 } }
                          }}
                        />
                      )}
                    </Grid>
                    <Grid size={4} style={{ minWidth: 0 }}>
                      {!isEdit ? (
                        <Typography
                          color="textSecondary"
                          style={ValueTypoStyle}
                          data-edit-index={index}
                          data-edit-field="value"
                        >
                          {value ? decimalToString(value) : "-"}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={value}
                          autoFocus={
                            _.get(fieldToFocus, "index") === index &&
                            _.get(fieldToFocus, "field") === "value"
                          }
                          onChange={createFieldOnChangeHandler(index, "value")}
                          slotProps={{
                            input: {
                              inputComponent: NumberFormatWrapper
                            }
                          }}
                        />
                      )}
                    </Grid>
                    <Grid size={3} style={{ minWidth: 0 }}>
                      {!isEdit ? (
                        <Typography
                          color="textSecondary"
                          style={ValueTypoStyle}
                          data-edit-index={index}
                          data-edit-field="monthlyPayment"
                        >
                          {monthlyPayment
                            ? decimalToString(monthlyPayment)
                            : "-"}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={monthlyPayment}
                          autoFocus={
                            _.get(fieldToFocus, "index") === index &&
                            _.get(fieldToFocus, "field") === "monthlyPayment"
                          }
                          onChange={createFieldOnChangeHandler(
                            index,
                            "monthlyPayment"
                          )}
                          slotProps={{
                            input: {
                              inputComponent: NumberFormatWrapper
                            }
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </ColoredTopBorderCard>
      </Grid>
    </Grid>
  );
};

export default injectIntl(InputTableGrid);
